<template lang="pug">
  .general-settings
    v-container.white.px-6(style="max-width:600px")
      v-layout.my-2
        div.mr-2
          v-icon.mt-1(left color="black" :size="24" @click="$router.push('/admin/settings')") mdi-arrow-left
        div.title.font-weight-bold {{ $t('general') }}

      v-card.mt-4(flat style="border:1px solid var(--v-basic300); border-radius:0" v-if="showCompanyLogo")
        v-card-title.py-0
          v-row(no-gutters align="center")
            v-col.py-2
              div.s1.basic1000--text {{ $t('company_logo') }}
            v-col.shrink 
              v-btn.px-0(v-if="!resizeImageFileUrl" color='transparent' text :ripple="false" :loading="isLoading")
                eva-icon.my-2(name="edit-outline" :fill="$vuetify.theme.themes[$theme].textColorAccent" height="17")
                .button-Aa-small.textColorAccent--text| {{ $t('change_image') }}
                input.input-layout(type="file" @change="onFileChange" accept="image/*" style="width: 100% !important; cursor: pointer !important;")
        v-divider.basic300
        v-card-text 
          v-card.mb-3(v-if="isLoading" height="203" flat)
            v-row.fill-height.ma-0(align='center' justify='center')
              v-progress-circular.ma-auto(:width="2" :size="50" color="primary" indeterminate)
          v-card.mb-3(v-else height="203" flat)
            div(v-if="resizeImageFileUrl" align="center")
              v-img(:src="resizeImageFileUrl" aspect-ratio="1" width="202")
            div.fill-height(v-else)
              div.d-flex.justify-center.align-end(style="height: 70%")
                div
                  div.file-layout.mb-4
                    v-btn.button-AA-tiny.text-uppercase(color="textColorAccent" outlined x-small height="24" :style="{'background-color': $vuetify.theme.themes[$theme].infoT08}") {{ $t('upload_image') }}
                    input.input-layout(type="file" @change="onFileChange" accept="image/*")
              div.d-flex.justify-center.align-end(style="height: 30%")
                div.mb-4
                  div.c1.basic600--text.text-center {{ $t('max_image_filesize') }}
                  div.c1.basic600--text.text-center {{ $t('recommended_image_size') }}

      v-card.mt-4(flat style="border:1px solid var(--v-basic300); border-radius:0")
        v-card-title.py-1
          v-col.pa-0
            v-row(no-gutters align="center")
              v-col
                div.s1.basic1000--text {{ $t('company_details') }}
              v-col.shrink 
                v-btn.px-0(color='transparent' text :ripple="false" @click="showUpdateCompanyDetails =! showUpdateCompanyDetails" small)
                  eva-icon(name="edit-outline" :fill="$vuetify.theme.themes[$theme].textColorAccent" height="17")
                  .button-Aa-small.textColorAccent--text| {{ $t('edit_details') }}
            div.p2.primary400--text(style="word-break: break-word") {{ $t('company_description') }}
        v-divider.basic300.mt-2
        v-card-text 
          v-layout.my-6(v-if="isFetching")
            v-row.fill-height.ma-0(align='center' justify='center')
              v-progress-circular.ma-auto(:width="2" :size="50" color="primary" indeterminate)
          div(v-else-if="latestCompany")          
            div.p2.primary500--text {{ latestCompany.name }}
            div.p2.primary500--text.my-2(v-if="latestCompany.registration_number") ( {{ latestCompany.registration_number }} )
            .address(v-if="latestCompany.address")
              div.p2.primary500--text {{ latestCompany.address.address_1 }},
              div.p2.primary500--text(v-if="latestCompany.address.address_2") {{ latestCompany.address.address_2 }},
              template(v-if="latestCompany.address.area")
                div.p2.primary500--text {{ latestCompany.address.postcode }} {{ latestCompany.address.area }},
                div.p2.primary500--text {{ latestCompany.address.suburb }}, {{ latestCompany.address.city }},
              div.p2.primary500--text(v-else) {{ latestCompany.address.postcode }} {{ latestCompany.address.city }},
              div.p2.primary500--text  {{ latestCompany.address.state }}, {{ latestCompany.address.country }}.
            div.p2.primary500--text.mt-2 {{ latestCompany.phone }}
            div.p2.primary500--text.mt-2 {{ $t('default_tax_rate') }}: {{ latestCompany.tax_rate || 0 }}%

    .modals
      update-company-details(v-if="showUpdateCompanyDetails" :open.sync="showUpdateCompanyDetails" :company.sync="latestCompany")

</template>

<script>
import { mapActions } from 'vuex'
import UpdateCompanyDetails from '@/components/dialogs/settings/UpdateCompanyDetails.vue'
import { resizeImage } from '@/utils'

export default {
  name: 'GeneralSettings',
  components: { UpdateCompanyDetails },
  props: ['company'],
  data (v) {
    return {
      isLoading: false,
      isFetching: false,
      latestCompany: null,
      showUpdateCompanyDetails: false,
      resizeImageFileUrl: v.company.logo ? v.company.logo.file : '',
      resizeImageFile: null
    }
  },
  computed: {
    showCompanyLogo () {
      return false
    }
  },
  mounted () {
    this.fetchSettings()
  },
  methods: {
    ...mapActions([
      'showMessage'
    ]),
    fetchSettings() {
      this.isFetching = true
      this.$axios.get(`/companies/${this.company.id}/`)
        .then((response) => {
          this.latestCompany = response.data
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    saveImage(){
      if (!this.resizeImageFile) {
        this.showMessage(['error', this.$t('error_message.no_image_found')])
        return
      }
      var params = new FormData()
      params.append('logo', this.resizeImageFile)
      this.axios.patch(`/companies/${this.company.id}/`, params)
        .then(() => {
          this.showMessage(['success', this.$t('success_message.image_updated')])
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_upload_image')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    async onFileChange (e) {
      this.isLoading = true
      const files = e.target.files || e.dataTransfer.files
      if (!files.length) { return }
      const resizedImage = await resizeImage(files[0], 'product_image.jpeg')
      this.resizeImageFileUrl = resizedImage.dataURL
      this.resizeImageFile = resizedImage.file
      setTimeout(() => this.saveImage(), 1500);
    }
  }
}
</script>
<style scoped>
.file-layout {
  position: relative;
  cursor: pointer;
}
.input-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
}
</style>
<i18n>
{
  "en": {
    "company_description": "This informations will be used in your company invoice"
  },
  "id": {
    "company_description": "Informasi ini akan digunakan dalam invoice perusahaan"
  }
}
</i18n>